import {TrackingService} from '@shared/services/TrackingService'
import type {KeyboardEvent} from 'react'
import {getFailSafeLocalStorage} from 'swayok-react-mdb-ui-kit/helpers/local_storage/getFailSafeLocalStorage'
import {DateTimeService} from 'swayok-react-mdb-ui-kit/services/DateTimeService'
import {UserBehaviorService} from 'swayok-react-mdb-ui-kit/services/UserBehaviorService'
import type {AnyObject} from 'swayok-react-mdb-ui-kit/types/Common'

// Имя шага.
export const enum BehaviorStepName {
    LOGIN = 'login',
    PREREG = 'preReg', // Аналог STEP1.
    STEP1 = 'reg1',
    STEP2 = 'reg2',
    STEP3 = 'reg3',
    STEP4 = 'reg4',
    SIGN = 'sign',
}

// Сбор данные о поведении пользователя в формах заполнения данных клиента CRM.
export class ClientBehaviorServiceClass {

    // Ключ в LocalStorage.
    localStorageKey = 'behavior'
    // Текущий шаг.
    currentStep: CurrentStepBehavior | null = null
    // Поведение активного поля ввода.
    activeInput: ActiveInputBehaviorData | null = null
    // Формат даты и времени.
    dateTimeFormat: string = 'YYYY-MM-DD HH:mm:ss'

    // Начало отслеживания поведения для шага.
    startStep(name: BehaviorStepName) {
        UserBehaviorService.setHandlers(this)
        this.currentStep = {
            name,
            begin: DateTimeService.now().utc().format(this.dateTimeFormat),
            fields: {},
            end: null,
        }
        // console.log('[ClientBehaviorService] startStep', this.currentStep)
    }

    // Завершение отслеживания поведения для шага.
    finishStep() {
        if (this.currentStep) {
            this.finishInputTracking()
            this.currentStep.end = DateTimeService.now().utc().format(this.dateTimeFormat)
            this.saveStepToLocalStorage()
        }
        UserBehaviorService.unsetHandlers()
        // console.log('[ClientBehaviorService] finishStep', this.currentStep)
    }

    // Обработка события: начало взаимодействия с полем ввода.
    onFocus = (
        input: ActiveInputBehaviorData['input'],
        id: string,
        value?: string | number | null
    ) => {
        this.startInputTracking(input, id, value)
        // console.log('[ClientBehaviorService] onFocus', id, input.value, value, this.activeInput)
    }

    // Обработка события: завершение взаимодействия с полем ввода.
    onBlur = (value?: string | null) => {
        // const activeInput =
        this.finishInputTracking(value)
        // console.log('[ClientBehaviorService] onBlur', activeInput?.id, value, activeInput, this.currentStep)
    }

    // Обработка события: вставка текста в поле ввода.
    onPaste = () => {
        this.rememberPasteAction()
        // console.log('[ClientBehaviorService] onPaste', this.activeInput?.id)
    }

    // Обработка события: нажатие на клавишу в поле ввода.
    onKeyDown = (event: KeyboardEvent<ActiveInputBehaviorData['input']>) => {
        if (event.key === 'Delete' || event.key === 'Backspace') {
            this.rememberTextDeleteAction()
        }
        // console.log('[ClientBehaviorService] onKeydown', this.activeInput?.id, event.key, this.activeInput)
    }

    // Обработка изменения значения в <input type="checkbox"/> и <input type="radio"/>
    onCheckboxOrRadioChange = (input: HTMLInputElement, id: string) => {
        this.startInputTracking(input, id)
        if (this.activeInput) {
            this.activeInput.oldValue = input.checked ? null : input.value
            // const activeInput =
            this.finishInputTracking()
            // console.log('[ClientBehaviorService] onCheckboxOrRadioChange', id, activeInput)
        }
    }

    // Отправка данных по поведению пользователя в формах в API.
    sendDataToApi() {
        try {
            TrackingService.trackBehavior(this.getLocalStorage().getItem(this.localStorageKey))
        } catch (error) {
            console.error('[ClientBehaviorService][sendDataToApi] error', error)
        }
    }

    // Регистрация шага.
    private saveStepToLocalStorage() {
        let data: LocalStorageData = {}
        try {
            const json: string | null = this.getLocalStorage().getItem(this.localStorageKey)
            data = json ? JSON.parse(json) : {}
            if (typeof data !== 'object' || data === null) {
                data = {}
            }
        } catch (_e) {
            data = {}
        }
        if (data.steps === undefined) {
            data.steps = {}
        }

        if (this.currentStep) {
            const {
                name,
                ...stepData
            } = this.currentStep
            if (
                data.steps[name] === undefined
                || !Array.isArray(data.steps[name])
            ) {
                data.steps[name] = []
            }
            data.steps[name].push(stepData)
        }
        this.getLocalStorage().setItem(this.localStorageKey, JSON.stringify(data))
    }

    // Начало отслеживания поведения пользователя при заполнении поля ввода.
    private startInputTracking(
        input: ActiveInputBehaviorData['input'],
        id: string,
        value?: string | number | null
    ) {
        this.activeInput = {
            input,
            id,
            begin: DateTimeService.now().utc().format(this.dateTimeFormat),
            oldValue: value === undefined
                ? this.getInputValue(input)
                : (value === null ? null : String(value)),
            value: null,
            end: null,
            paste: false,
            deletes: 0,
        }
    }

    // Регистрация факта "вставки" значения в поле.
    private rememberPasteAction() {
        if (this.activeInput) {
            this.activeInput.paste = true
        }
    }

    // Регистрация факта стирания значения из поля.
    private rememberTextDeleteAction() {
        if (this.activeInput) {
            this.activeInput.deletes++
        }
    }

    // Окончание отслеживания поведения пользователя при заполнении поля ввода.
    private finishInputTracking(value?: string | null): ActiveInputBehaviorData | null {
        if (this.activeInput) {
            this.activeInput.value = value === undefined
                ? this.getInputValue(this.activeInput.input)
                : value
            this.activeInput.end = DateTimeService.now().utc().format(this.dateTimeFormat)

            if (this.currentStep) {
                if (!Array.isArray(this.currentStep.fields[this.activeInput.id])) {
                    this.currentStep.fields[this.activeInput.id] = []
                }

                const {
                    input,
                    ...fieldData
                } = this.activeInput
                this.currentStep.fields[this.activeInput.id].push(fieldData)
            }
            const ret = this.activeInput
            this.activeInput = null
            return ret
        }
        return null
    }

    // Получить значение из поля ввода.
    private getInputValue(input: ActiveInputBehaviorData['input']): string | null {
        switch (input.type) {
            case 'checkbox':
                return (input as HTMLInputElement).checked ? input.value : null
            default:
                return input.value
        }
    }

    // Получить объект LocalStorage.
    // Стоит учитывать, что доступ к LocalStorage может быть ограничен и
    // будет вызывать ошибки вида:
    // Failed to read the 'localStorage' property from 'Window': Access is denied for this document.
    // Для этого требуется либо использовать DummyStorage (ничего не хранить),
    // либо FallbackStorage (хранить в массиве до перезагрузки страницы).
    private getLocalStorage(): Storage {
        return getFailSafeLocalStorage()
    }
}

export const ClientBehaviorService = new ClientBehaviorServiceClass()

// Данные о поведении пользователя в форме заполнения данных клиента CRM.
interface StepBehaviorData {
    begin: string // date-time
    fields: AnyObject<Omit<InputBehaviorData, 'input'>[]>
    end: string | null // date-time
}

// Данные о поведении пользователя при взаимодействии с полем ввода.
interface InputBehaviorData {
    id: string
    begin: string // date-time
    oldValue: string | null
    value: string | null
    end: string | null // date-time
    paste: boolean
    deletes: number
}

// Данные активного поля ввода.
interface ActiveInputBehaviorData extends InputBehaviorData {
    input: HTMLInputElement | HTMLTextAreaElement
}

// Данные текущего шага.
interface CurrentStepBehavior extends StepBehaviorData {
    name: BehaviorStepName
}

// Данные для сохранения в window.localStorage.
interface LocalStorageData {
    steps?: AnyObject<StepBehaviorData[], BehaviorStepName>
}
